import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import ChessClockSale from "./ChessClockSale";
import ResponsiveAppMenu from "./ResponsiveAppMenu";

const START_PAWN = {
  x: 49.5,
  y: 71.5,
  scale: 1,
  opacity: 1,
};

const WALK_DURATION_MS = 950;
const ENTER_DURATION_MS = 430;
const PAWN_VISUAL_CENTER_X = 50.5;
const PAWN_VISUAL_CENTER_Y = 48.9;
const PAWN_MIN_WIDTH_PX = 200;
const PAWN_MAX_WIDTH_PX = 430;
const PAWN_VIEWPORT_WIDTH_RATIO = 0.36;
const PAWN_ASPECT_RATIO = 1536 / 1024;

const DOORS = [
  {
    id: "clubs",
    label: "Clubs",
    route: "/chessclub",
    hotspot: { x: 3.1, y: 21.8, width: 17.6, height: 26.8 },
    target: { x: 14.7, y: 53.5, scale: 0.74, opacity: 1 },
    enter: { x: 14.7, y: 48.5, scale: 0.2, opacity: 0 },
  },
  {
    id: "lessons",
    label: "Lessons",
    route: "/meetings",
    hotspot: { x: 22.9, y: 29.4, width: 13.9, height: 22.8 },
    target: { x: 32.5, y: 56.5, scale: 0.64, opacity: 1 },
    enter: { x: 32.5, y: 52, scale: 0.18, opacity: 0 },
  },
  {
    id: "reception",
    label: "Reception",
    route: "/contact",
    hotspot: { x: 60.7, y: 23.8, width: 14.8, height: 27.5 },
    target: { x: 67, y: 56.5, scale: 0.64, opacity: 1 },
    enter: { x: 67, y: 52, scale: 0.18, opacity: 0 },
  },
  {
    id: "training",
    label: "Training",
    route: "/hood-ai",
    hotspot: { x: 79.4, y: 16.6, width: 15.7, height: 33.7 },
    target: { x: 88.1, y: 52.2, scale: 0.72, opacity: 1 },
    enter: { x: 88.1, y: 46.5, scale: 0.18, opacity: 0 },
  },
];

const HOME_COPY = {
  title: {
    en: "Chesshood | Chess Training, Lessons and Clubs",
    pl: "Chesshood | Trening szachowy, lekcje i kluby",
    es: "Chesshood | Entrenamiento, clases y clubes de ajedrez",
  },
  description: {
    en: "Chesshood is a chess training platform with puzzle training, private lessons, junior chess clubs, camps and contact routes for players and families.",
    pl: "Chesshood to platforma szachowa z treningiem zadan, lekcjami, klubami juniorskimi, obozami i kontaktem dla graczy oraz rodzin.",
    es: "Chesshood es una plataforma de ajedrez con entrenamiento de puzzles, clases privadas, clubes junior, campamentos y contacto para jugadores y familias.",
  },
  menu: {
    home: { en: "Home", pl: "Start", es: "Inicio" },
    training: { en: "Hood AI", pl: "Hood AI", es: "Hood AI" },
    lessons: { en: "Lessons", pl: "Lekcje", es: "Clases" },
    reception: { en: "Contact", pl: "Kontakt", es: "Contacto" },
    rules: { en: "Rules", pl: "Zasady", es: "Reglas" },
    club: { en: "Club", pl: "Klub", es: "Club" },
  },
  door: {
    clubs: { en: "Clubs", pl: "Kluby", es: "Clubes" },
    lessons: { en: "Lessons", pl: "Lekcje", es: "Clases" },
    reception: { en: "Contact", pl: "Kontakt", es: "Contacto" },
    training: { en: "Hood AI", pl: "Hood AI", es: "Hood AI" },
  },
  badge: {
    en: "Click a door and Hood will walk inside.",
    pl: "Kliknij drzwi, a Hood wejdzie do srodka.",
    es: "Haz clic en una puerta y Hood entrara dentro.",
  },
  entering: {
    en: "Entering",
    pl: "Wchodze do",
    es: "Entrando en",
  },
  chooseRoom: {
    en: "Choose your room",
    pl: "Wybierz pokoj",
    es: "Elige tu sala",
  },
  infoTitle: {
    en: "Chesshood chess training on web and mobile",
    pl: "Chesshood trening szachowy na web i mobile",
    es: "Chesshood entrenamiento de ajedrez en web y movil",
  },
  infoCopy: {
    en: "Chesshood helps players train with chess puzzles, improve practical decision-making, book lessons, and find junior club information in Winchester and nearby areas.",
    pl: "Chesshood pomaga trenowac na zadaniach szachowych, poprawiac praktyczne decyzje, rezerwowac lekcje i znajdowac informacje o klubach juniorskich w Winchester i okolicy.",
    es: "Chesshood ayuda a entrenar con puzzles, mejorar decisiones practicas, reservar clases y encontrar informacion sobre clubes junior en Winchester y alrededores.",
  },
  cards: {
    trainingTitle: { en: "Hood AI", pl: "Hood AI", es: "Hood AI" },
    trainingCopy: {
      en: "Meet your six AI coaches. Start with six puzzles and a game with Hood.",
      pl: "Poznaj sześciu trenerów AI. Zacznij od sześciu zadań i partii z Hoodem.",
      es: "Conoce a tus seis entrenadores IA. Empieza con seis ejercicios y una partida con Hood.",
    },
    lessonsTitle: { en: "Lessons", pl: "Lekcje", es: "Clases" },
    lessonsCopy: {
      en: "Private online chess lessons with clear goals and follow-up work.",
      pl: "Prywatne lekcje online z jasnym celem i dalsza praca.",
      es: "Clases privadas online con objetivos claros y seguimiento.",
    },
    clubsTitle: { en: "Clubs and camps", pl: "Kluby i obozy", es: "Clubes y campamentos" },
    clubsCopy: {
      en: "Local junior chess clubs, tournaments and camps for families.",
      pl: "Lokalne kluby juniorskie, turnieje i obozy dla rodzin.",
      es: "Clubes junior locales, torneos y campamentos para familias.",
    },
  },
  adults: {
    title: {
      en: "Adult beginners",
      pl: "Dorosli poczatkujacy",
      es: "Adultos principiantes",
    },
    copy: {
      en: "If you always wanted to play chess but felt that a club session might be too strong at first, start here. These adult group courses build confidence through guided basics, practical play and a clear study routine.",
      pl: "Jesli zawsze chciales grac w szachy, ale czules, ze klub na poczatku moze byc zbyt trudny, zacznij tutaj. Te grupowe kursy dla doroslych buduja pewnosc siebie przez spokojne podstawy, praktyczna gre i jasny plan nauki.",
      es: "Si siempre quisiste jugar al ajedrez pero sentias que un club podia ser demasiado fuerte al principio, empieza aqui. Estos cursos grupales para adultos construyen confianza con bases guiadas, juego practico y una rutina clara de estudio.",
    },
    offerTitle: {
      en: "Group courses for adults",
      pl: "Grupowe kursy dla doroslych",
      es: "Cursos grupales para adultos",
    },
    fee: {
      en: "One course fee. Attend Badger Farm Community Centre, Colden Common, or both.",
      pl: "Jedna oplata za kurs. Mozesz chodzic do Badger Farm Community Centre, Colden Common albo do obu miejsc.",
      es: "Una sola cuota del curso. Puedes asistir a Badger Farm Community Centre, Colden Common o a ambos.",
    },
    offers: {
      en: [
        "Badger Farm Community Centre, Thursday, 7pm to 8pm",
        "Colden Common, Friday, 7pm to 8pm",
      ],
      pl: [
        "Badger Farm Community Centre, czwartek, 7pm do 8pm",
        "Colden Common, piatek, 7pm do 8pm",
      ],
      es: [
        "Badger Farm Community Centre, jueves, 7pm a 8pm",
        "Colden Common, viernes, 7pm a 8pm",
      ],
    },
    skills: {
      en: "Openings, middlegame, endgames, calculation, notation, analysis and how to train well.",
      pl: "Otwarcia, gra srodkowa, koncowki, kalkulacja, zapis partii, analiza i to, jak dobrze trenowac.",
      es: "Aperturas, juego medio, finales, calculo, anotacion, analisis y como entrenar bien.",
    },
    linksTitle: {
      en: "Adult routes",
      pl: "Sciezki dla doroslych",
      es: "Rutas para adultos",
    },
    book: {
      en: "Bookwhen list",
      pl: "Lista Bookwhen",
      es: "Lista Bookwhen",
    },
    bookList: {
      en: "Book now",
      pl: "Book now",
      es: "Book now",
    },
    bookListCopy: {
      en: "Choose one or several sessions from the current Bookwhen list.",
      pl: "Wybierz jeden lub kilka terminow z aktualnej listy Bookwhen.",
      es: "Elige una o varias sesiones de la lista actual de Bookwhen.",
    },
    training: {
      en: "Training",
      pl: "Trening",
      es: "Entrenamiento",
    },
    lessons: {
      en: "Lessons",
      pl: "Lekcje",
      es: "Clases",
    },
    contact: {
      en: "Reception",
      pl: "Recepcja",
      es: "Recepcion",
    },
  },
  links: {
    about: { en: "About us", pl: "O nas", es: "Sobre nosotros" },
    contact: { en: "Contact", pl: "Kontakt", es: "Contacto" },
    privacy: { en: "Privacy Policy", pl: "Polityka prywatnosci", es: "Politica de privacidad" },
    terms: { en: "Subscription Terms", pl: "Warunki subskrypcji", es: "Terminos de suscripcion" },
  },
};

const APP_BADGES = [
  {
    id: "app-store",
    label: "Download on the App Store",
    href: "https://apps.apple.com/app/chesshood-ai-chess-trainer/id6751606749",
    hotspot: { x: 38.2, y: 81.8, width: 20.1, height: 4.9 },
  },
  {
    id: "google-play",
    label: "Get it on Google Play",
    href: "https://play.google.com/store/apps/details?id=com.lucas7602.chesstreining",
    hotspot: { x: 61.0, y: 81.8, width: 21.0, height: 4.9 },
  },
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const getPawnBaseWidth = (viewportWidth) =>
  clamp(
    viewportWidth * PAWN_VIEWPORT_WIDTH_RATIO,
    PAWN_MIN_WIDTH_PX,
    PAWN_MAX_WIDTH_PX
  );

const createWalkPose = ({ clickX, clickY, sceneRect, viewportWidth }) => {
  const normalizedY = clamp(clickY, 0, 100);
  const scale = 0.56 + (normalizedY / 100) * 0.44;
  const pawnWidthPx = getPawnBaseWidth(viewportWidth) * scale;
  const pawnHeightPx = pawnWidthPx * PAWN_ASPECT_RATIO;
  const offsetXPct =
    (((PAWN_VISUAL_CENTER_X - 50) / 100) * pawnWidthPx * 100) / sceneRect.width;
  const offsetYPct =
    (((100 - PAWN_VISUAL_CENTER_Y) / 100) * pawnHeightPx * 100) /
    sceneRect.height;

  return {
    x: clickX - offsetXPct,
    y: clickY + offsetYPct,
    scale,
    opacity: 1,
  };
};

function HomeEntranceHero() {
  const navigate = useNavigate();
  const { pick } = useLanguage();
  const [pawnPose, setPawnPose] = useState(START_PAWN);
  const [activeDoorId, setActiveDoorId] = useState(null);
  const timersRef = useRef([]);

  useEffect(() => {
    document.title = pick(HOME_COPY.title);

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }

    metaDesc.setAttribute(
      "content",
      pick(HOME_COPY.description)
    );

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [pick]);

  const activeDoor = DOORS.find((door) => door.id === activeDoorId) || null;

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const startDoorJourney = (door, destinationRoute = door.route) => {
    if (activeDoorId) {
      return;
    }

    clearTimers();
    setActiveDoorId(door.id);
    setPawnPose(door.target);

    timersRef.current.push(
      setTimeout(() => {
        setPawnPose(door.enter);
      }, WALK_DURATION_MS)
    );

    timersRef.current.push(
      setTimeout(() => {
        navigate(destinationRoute);
      }, WALK_DURATION_MS + ENTER_DURATION_MS)
    );
  };

  const handleDoorClick = (door) => {
    startDoorJourney(door);
  };

  const handleSceneClick = (event) => {
    if (activeDoorId) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    clearTimers();
    setPawnPose(
      createWalkPose({
        clickX: x,
        clickY: y,
        sceneRect: rect,
        viewportWidth: window.innerWidth,
      })
    );
  };

  return (
    <section className="ch-homehole-hero" aria-label="Chesshood main entrance">
      <style>{`
        .ch-homehole-hero{
          width:100%;
          padding:clamp(16px, 2.5vw, 28px);
          background:
            radial-gradient(circle at top, rgba(255,208,90,0.2), transparent 34%),
            linear-gradient(180deg, #0d397b 0%, #0d397b 100%);
        }

        .ch-homehole-accountbar{
          width:min(100%, 960px);
          margin:0 auto 10px;
          display:flex;
          justify-content:flex-end;
          align-items:center;
          gap:8px;
          position:relative;
          z-index:10;
        }

        .ch-homehole-frame{
          width:min(100%, 960px);
          margin:0 auto;
          border-radius:28px;
          overflow:hidden;
          box-shadow:0 28px 60px rgba(0, 0, 0, 0.28);
          border:1px solid rgba(255,255,255,0.12);
          background:#0d397b;
        }

        .ch-homehole-scene{
          position:relative;
          width:100%;
          aspect-ratio:1024 / 1536;
          background-color:#0d397b;
          background-image:url('/homehole.png');
          background-size:cover;
          background-position:center;
          isolation:isolate;
        }

        .ch-homehole-scene::after{
          content:"";
          position:absolute;
          inset:0;
          background:linear-gradient(180deg, rgba(4,10,26,0.08) 0%, rgba(4,10,26,0.02) 50%, rgba(4,10,26,0.18) 100%);
          pointer-events:none;
          z-index:0;
        }

        .ch-homehole-copy{
          position:absolute;
          left:clamp(14px, 2.2vw, 24px);
          right:clamp(14px, 2.2vw, 24px);
          bottom:clamp(14px, 2.2vw, 24px);
          display:flex;
          justify-content:space-between;
          gap:12px;
          align-items:flex-end;
          z-index:3;
          pointer-events:none;
        }

        .ch-homehole-badge,
        .ch-homehole-status{
          border-radius:999px;
          padding:10px 14px;
          color:#fff7d4;
          font-weight:800;
          letter-spacing:0.02em;
          background:rgba(7, 18, 41, 0.72);
          border:1px solid rgba(255, 213, 116, 0.3);
          box-shadow:0 8px 22px rgba(0, 0, 0, 0.18);
          backdrop-filter:blur(6px);
        }

        .ch-homehole-badge{
          max-width:62%;
        }

        .ch-homehole-status{
          text-align:right;
          min-width:max-content;
        }

        .ch-homehole-door{
          position:absolute;
          z-index:2;
          border:0;
          padding:0;
          background:transparent;
          cursor:pointer;
          border-radius:36px 36px 18px 18px;
          outline:none;
          transition:transform 180ms ease;
        }

        .ch-homehole-door::before{
          content:"";
          position:absolute;
          inset:0;
          border-radius:24px;
          background:linear-gradient(180deg, rgba(255, 200, 74, 0.08), rgba(255, 200, 74, 0.16));
          opacity:0;
          transition:opacity 180ms ease;
        }

        .ch-homehole-door::after{
          content:"";
          position:absolute;
          inset:8%;
          border-radius:30px 30px 14px 14px;
          border:2px solid rgba(255, 213, 116, 0.6);
          opacity:0;
          transition:opacity 180ms ease;
        }

        .ch-homehole-door:hover::before,
        .ch-homehole-door:hover::after,
        .ch-homehole-door:focus-visible::before,
        .ch-homehole-door:focus-visible::after,
        .ch-homehole-door.is-active::before,
        .ch-homehole-door.is-active::after{
          opacity:1;
        }

        .ch-homehole-door:hover,
        .ch-homehole-door:focus-visible{
          transform:scale(1.02);
        }

        .ch-homehole-door:disabled{
          cursor:default;
        }

        .ch-homehole-menu-button{
          position:absolute;
          z-index:4;
          border:0;
          padding:0;
          background:transparent;
          cursor:pointer;
          border-radius:12px 12px 18px 18px;
          transition:transform 180ms ease, box-shadow 180ms ease;
        }

        .ch-homehole-menu-button::before{
          content:"";
          position:absolute;
          inset:0;
          border-radius:inherit;
          background:linear-gradient(180deg, rgba(255, 208, 73, 0.08), rgba(255, 208, 73, 0.18));
          opacity:0;
          transition:opacity 180ms ease;
        }

        .ch-homehole-menu-button:hover,
        .ch-homehole-menu-button:focus-visible{
          transform:translateY(1px);
          box-shadow:0 0 0 2px rgba(255, 220, 120, 0.42);
        }

        .ch-homehole-menu-button:hover::before,
        .ch-homehole-menu-button:focus-visible::before{
          opacity:1;
        }

        .ch-homehole-menu-button:disabled{
          cursor:default;
          opacity:0.6;
        }

        .ch-homehole-owner-beta{
          position:absolute;
          z-index:6;
          top:6.1%;
          right:2.2%;
          border:1px solid rgba(125,211,252,.72);
          border-radius:999px;
          padding:8px 12px;
          background:rgba(7,18,41,.88);
          color:#7dd3fc;
          font-size:clamp(.68rem,1.2vw,.82rem);
          font-weight:900;
          letter-spacing:.04em;
          text-decoration:none;
          box-shadow:0 8px 22px rgba(0,0,0,.24);
          backdrop-filter:blur(6px);
        }

        .ch-homehole-owner-beta:hover,
        .ch-homehole-owner-beta:focus-visible{
          background:rgba(14,116,144,.34);
          color:#e0f2fe;
          outline:none;
          box-shadow:0 0 0 2px rgba(125,211,252,.38);
        }

        .ch-homehole-store-link{
          position:absolute;
          z-index:4;
          display:block;
          border-radius:12px;
          background:transparent;
          text-decoration:none;
          box-shadow:none;
          transition:transform 180ms ease, box-shadow 180ms ease, background 180ms ease;
        }

        .ch-homehole-store-link::before{
          content:"";
          position:absolute;
          inset:0;
          border-radius:inherit;
          background:rgba(255, 255, 255, 0.08);
          opacity:0;
          transition:opacity 180ms ease;
        }

        .ch-homehole-store-link:hover,
        .ch-homehole-store-link:focus-visible{
          transform:translateY(-1px);
          box-shadow:0 0 0 2px rgba(255, 255, 255, 0.28);
        }

        .ch-homehole-store-link:hover::before,
        .ch-homehole-store-link:focus-visible::before{
          opacity:1;
        }

        .ch-homehole-store-link.is-disabled{
          pointer-events:none;
          opacity:0.6;
        }

        .ch-homehole-door-label{
          position:absolute;
          left:50%;
          bottom:-18px;
          transform:translateX(-50%);
          padding:7px 12px;
          border-radius:999px;
          background:rgba(7, 18, 41, 0.82);
          color:#fff7d4;
          font-size:clamp(0.72rem, 1.2vw, 0.95rem);
          font-weight:800;
          letter-spacing:0.03em;
          white-space:nowrap;
          opacity:0;
          transition:opacity 180ms ease, transform 180ms ease;
          box-shadow:0 8px 20px rgba(0,0,0,0.22);
          pointer-events:none;
        }

        .ch-homehole-door:hover .ch-homehole-door-label,
        .ch-homehole-door:focus-visible .ch-homehole-door-label,
        .ch-homehole-door.is-active .ch-homehole-door-label{
          opacity:1;
          transform:translateX(-50%) translateY(-4px);
        }

        .ch-homehole-pawn{
          position:absolute;
          z-index:2;
          width:clamp(200px, 36vw, 430px);
          pointer-events:none;
          filter:drop-shadow(0 16px 22px rgba(0, 0, 0, 0.45));
          transition:
            left ${WALK_DURATION_MS}ms cubic-bezier(0.25, 0.8, 0.25, 1),
            top ${WALK_DURATION_MS}ms cubic-bezier(0.25, 0.8, 0.25, 1),
            transform ${WALK_DURATION_MS}ms cubic-bezier(0.25, 0.8, 0.25, 1),
            opacity ${ENTER_DURATION_MS}ms ease;
        }

        .ch-homehole-pawn.is-entering{
          filter:drop-shadow(0 6px 10px rgba(0, 0, 0, 0.22));
        }

        @media (max-width: 768px){
          .ch-homehole-accountbar{
            width:100%;
            padding:8px 10px;
            margin:0;
            background:#0d397b;
          }

          .ch-homehole-hero{
            padding:0;
            min-height:100svh;
            background:#0d397b;
          }

          .ch-homehole-frame{
            width:100vw;
            min-height:100svh;
            border-radius:0;
            border:0;
            box-shadow:none;
            background:#0d397b;
          }

          .ch-homehole-scene{
            min-height:100svh;
            aspect-ratio:auto;
            background-size:100% 100%;
            background-color:#0d397b;
          }

          .ch-homehole-copy{
            display:none;
          }

          .ch-homehole-badge,
          .ch-homehole-status{
            max-width:100%;
            min-width:0;
            text-align:left;
          }

          .ch-homehole-door-label{
            display:none;
          }
        }

        .ch-homehole-info{
          width:min(100%, 960px);
          margin:18px auto 0;
          padding:18px;
          border-radius:24px;
          border:1px solid rgba(255,255,255,0.12);
          background:linear-gradient(180deg, rgba(10, 22, 48, 0.92), rgba(8, 18, 38, 0.98));
          box-shadow:0 24px 48px rgba(0,0,0,0.22);
          color:#f2ead5;
        }

        .ch-homehole-info h1{
          margin:0 0 8px;
          color:#fff5dc;
          font-size:clamp(1.45rem, 2.2vw, 1.9rem);
        }

        .ch-homehole-info p{
          margin:0;
          color:#dfd3bb;
          line-height:1.65;
        }

        .ch-homehole-info-grid{
          display:grid;
          grid-template-columns:repeat(3, minmax(0, 1fr));
          gap:12px;
          margin-top:16px;
        }

        .ch-homehole-info-card{
          padding:14px;
          border-radius:18px;
          background:rgba(255,255,255,0.05);
          border:1px solid rgba(255,255,255,0.08);
        }

        .ch-homehole-info-card strong{
          display:block;
          margin-bottom:6px;
          color:#ffd76d;
        }

        .ch-homehole-info-links{
          display:flex;
          flex-wrap:wrap;
          gap:12px;
          margin-top:16px;
        }

        .ch-homehole-info-links a{
          color:#fff2cb;
          font-weight:800;
          text-decoration:none;
        }

        @media (max-width: 768px){
          .ch-homehole-info{
            margin-top:0;
            border-radius:24px 24px 0 0;
          }

          .ch-homehole-info-grid{
            grid-template-columns:1fr;
          }
        }
      `}</style>

      <div className="ch-homehole-accountbar">
        <ResponsiveAppMenu />
      </div>

      <div className="ch-homehole-frame">
        <div className="ch-homehole-scene" onClick={handleSceneClick}>

          {APP_BADGES.map((badge) => (
            <a
              key={badge.id}
              href={badge.href}
              onClick={(event) => event.stopPropagation()}
              className={`ch-homehole-store-link${
                activeDoorId ? " is-disabled" : ""
              }`}
              aria-label={badge.label}
              title={badge.label}
              style={{
                left: `${badge.hotspot.x}%`,
                top: `${badge.hotspot.y}%`,
                width: `${badge.hotspot.width}%`,
                height: `${badge.hotspot.height}%`,
              }}
            />
          ))}

          {DOORS.map((door) => {
            const isActive = activeDoorId === door.id;

            return (
              <button
                key={door.id}
                type="button"
                className={`ch-homehole-door${isActive ? " is-active" : ""}`}
                onClick={(event) => {
                  event.stopPropagation();
                  handleDoorClick(door);
                }}
                disabled={Boolean(activeDoorId)}
                aria-label={`Enter ${pick(HOME_COPY.door[door.id])}`}
                style={{
                  left: `${door.hotspot.x}%`,
                  top: `${door.hotspot.y}%`,
                  width: `${door.hotspot.width}%`,
                  height: `${door.hotspot.height}%`,
                }}
              >
                <span className="ch-homehole-door-label">
                  {pick(HOME_COPY.door[door.id])}
                </span>
              </button>
            );
          })}

          <img
            src="/hood.png"
            alt=""
            aria-hidden="true"
            className={`ch-homehole-pawn${activeDoorId ? " is-entering" : ""}`}
            style={{
              left: `${pawnPose.x}%`,
              top: `${pawnPose.y}%`,
              opacity: pawnPose.opacity,
              transform: `translate(-50%, -100%) scale(${pawnPose.scale})`,
            }}
          />

          <div className="ch-homehole-copy">
            <div className="ch-homehole-badge">
              {pick(HOME_COPY.badge)}
            </div>

            <div className="ch-homehole-status" aria-live="polite">
              {activeDoor
                ? `${pick(HOME_COPY.entering)} ${pick(
                    HOME_COPY.door[activeDoor.id]
                  )}...`
                : pick(HOME_COPY.chooseRoom)}
            </div>
          </div>
        </div>
      </div>

      <ChessClockSale />

      <div className="ch-homehole-info">
        <h1>{pick(HOME_COPY.infoTitle)}</h1>
        <p>
          {pick(HOME_COPY.infoCopy)}
        </p>

        <div className="ch-homehole-info-grid">
          <div className="ch-homehole-info-card">
            <strong>{pick(HOME_COPY.cards.trainingTitle)}</strong>
            {pick(HOME_COPY.cards.trainingCopy)}
          </div>
          <div className="ch-homehole-info-card">
            <strong>{pick(HOME_COPY.cards.lessonsTitle)}</strong>
            {pick(HOME_COPY.cards.lessonsCopy)}
          </div>
          <div className="ch-homehole-info-card">
            <strong>{pick(HOME_COPY.cards.clubsTitle)}</strong>
            {pick(HOME_COPY.cards.clubsCopy)}
          </div>
        </div>
        <div className="ch-homehole-info-links">
          <a href="/aboutus">{pick(HOME_COPY.links.about)}</a>
          <a href="/contact">{pick(HOME_COPY.links.contact)}</a>
          <a href="/privacy-policy">{pick(HOME_COPY.links.privacy)}</a>
          <a href="/subscription-terms">{pick(HOME_COPY.links.terms)}</a>
        </div>
      </div>
    </section>
  );
}

export default HomeEntranceHero;

